"use client";

import { useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { TextPreviewInput } from "./TextPreviewInput";
import { isTextPreviewContainer } from "@/utils/textPreview";
import { SlashVariableHint } from "./ComplexMarkdownField/ComplexMarkdownField";
import {
  CodeEditorSurface,
  type CodeEditorLanguage,
} from "./CodeEditor/CodeEditor";
import { CodeEditorDialog } from "./CodeEditor/CodeEditorDialog";
import {
  chipOptionsToEditorInputs,
  storedTextValue,
} from "./CodeEditor/templateVariables";
import type { AdvancedChipOption } from "./TiptapEditor/chipOptionsConfig";

/**
 * V1 renderer for a `rich_text_variables` field that declares a `language`:
 * the code editor V2's code fields use, with the node's ((variables)) as
 * chips. Edits store the text verbatim as a plain string, which the engine
 * reads as is.
 */
export function CodeVariablesField({
  control,
  name,
  label,
  tooltip,
  placeholder,
  options,
  language,
  numberOfLines,
  isDisabled,
}: {
  control: Control<any>;
  name: string;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  options: AdvancedChipOption[];
  language: CodeEditorLanguage;
  numberOfLines?: number;
  isDisabled?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputs = useMemo(() => chipOptionsToEditorInputs(options), [options]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur } }) => {
        const fieldLabel = label && (
          <NoxusFormLabel
            label={label}
            tooltip={tooltip}
            name={name}
            isDisabled={isDisabled}
          />
        );
        if (isTextPreviewContainer(value)) {
          return (
            <div className="w-full">
              {fieldLabel}
              <TextPreviewInput value={value} />
            </div>
          );
        }
        const text = storedTextValue(value);
        return (
          <div className="w-full">
            {fieldLabel}
            <CodeEditorSurface
              value={text}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={placeholder}
              isDisabled={isDisabled}
              inputs={inputs}
              language={language}
              minRows={numberOfLines}
              refSyntax="template"
              onExpand={isDisabled ? undefined : () => setIsExpanded(true)}
            />
            {inputs.length > 0 && <SlashVariableHint />}
            <CodeEditorDialog
              isOpen={isExpanded}
              onOpenChange={setIsExpanded}
              initialValue={text}
              onCommit={onChange}
              inputs={inputs}
              label={label}
              placeholder={placeholder}
              isDisabled={isDisabled}
              language={language}
              refSyntax="template"
            />
          </div>
        );
      }}
    />
  );
}
